import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { detectLocale, detectUrlLocale, persistLocale, applyDocumentDirection, clearLocale } from '../utils/localeDetection';
import { DEFAULT_LOCALE } from '../constants';
import { isLanguageSelectorEnabled } from '../components/Utils';

interface LocaleContextValue {
    locale: string;
    setLocale: (locale: string) => void;
    resetLocale: () => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

interface LocaleProviderProps {
    children: React.ReactNode;
}

/**
 * Get initial locale based on site allowlist and URL parameter.
 *
 * Priority:
 * 1. Check site allowlist FIRST - non-allowlisted sites always get English
 * 2. For allowlisted sites: URL ?lang= param > localStorage > browser > default
 *
 * The allowlist check comes first to ensure tight control during pilot rollout.
 * Only allowlisted sites can use non-English languages.
 */
function getInitialLocale(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const siteCode = urlParams.get('site_code');

    // Non-allowlisted sites: force English, ignore ?lang= param
    if (!isLanguageSelectorEnabled(siteCode)) {
        return DEFAULT_LOCALE;
    }

    // Allowlisted sites: honor ?lang= URL parameter first
    const urlLocale = detectUrlLocale();
    if (urlLocale) {
        return urlLocale;
    }

    // Then fall back to localStorage/browser detection
    return detectLocale();
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
    const [locale, setLocaleState] = useState<string>(() => getInitialLocale());

    const setLocale = useCallback((newLocale: string) => {
        persistLocale(newLocale);
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    const resetLocale = useCallback(() => {
        clearLocale();
        const newLocale = getInitialLocale();
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    // Apply document direction on initial mount based on detected locale
    useEffect(() => {
        applyDocumentDirection(locale);
    }, [locale]);

    const value = useMemo(
        () => ({ locale, setLocale, resetLocale }),
        [locale, setLocale, resetLocale]
    );

    return (
        <LocaleContext.Provider value={value}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = (): LocaleContextValue => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocaleProvider');
    }
    return context;
};
